/* global snaptr */

import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getAnalytics, logEvent } from "firebase/analytics";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "../../firebase-config";
import styles from "./PurchaseSuccess.module.css";

function getSnapClickId() {
  try {
    const p = new URLSearchParams(window.location.search);
    return p.get("sc_click_id") || undefined;
  } catch {
    return undefined;
  }
}

const PurchaseSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const analytics = getAnalytics(app);
  const functions = getFunctions(app);
  
  const [status, setStatus] = useState('loading'); // 'loading' | 'success' | 'error'
  const [orderDetails, setOrderDetails] = useState(null);
  const hasTracked = useRef(false);

  useEffect(() => {
    // Prevent double-firing in React StrictMode or re-renders
    if (hasTracked.current || !sessionId) {
      if (!sessionId) setStatus('error');
      return;
    }
    hasTracked.current = true;

    const fetchSessionAndTrack = async () => {
      try {
        // Fetch real order data from Stripe via Cloud Function
        const getStripeSessionDetails = httpsCallable(functions, 'getStripeSessionDetails');
        const result = await getStripeSessionDetails({ sessionId });
        
        const { amountTotal, currency, lineItems, customerEmail } = result.data;
        const totalPrice = amountTotal / 100; // Stripe amounts are in cents
        const itemCount = lineItems.reduce((sum, item) => sum + item.quantity, 0);
        const itemIds = lineItems.map(item => item.price?.product || item.id);

        setOrderDetails({
          totalPrice,
          currency: currency.toUpperCase(),
          itemCount,
          customerEmail
        });

        // Firebase Analytics - Purchase event (GA4 standard)
        logEvent(analytics, "purchase", {
          transaction_id: sessionId,
          currency: currency.toUpperCase(),
          value: totalPrice,
          items: lineItems.map((item, idx) => ({
            item_id: item.price?.product || `item_${idx}`,
            item_name: item.description || `Qwytt Item ${idx + 1}`,
            price: (item.amount_total / 100) / item.quantity,
            quantity: item.quantity
          }))
        });

        // Also log conversion event
        logEvent(analytics, "conversion", {
          send_to: "purchase",
          transaction_id: sessionId,
          value: totalPrice,
          currency: currency.toUpperCase()
        });

        // Log page view
        logEvent(analytics, "page_view", {
          page_title: "Purchase Success",
          page_location: window.location.href,
          page_path: "/success"
        });

        // Fire SNAP PURCHASE event with REAL price from Stripe
        if (window.snaptr) {
          snaptr("track", "PURCHASE", {
            price: totalPrice,
            currency: currency.toUpperCase(),
            transaction_id: sessionId,
            item_ids: itemIds,
            number_items: itemCount,
            uuid_c1: getSnapClickId(),
            user_email: customerEmail || undefined,
          });
          console.log("✅ Snap PURCHASE fired with price:", totalPrice);
        }

        setStatus('success');

      } catch (err) {
        console.error("Failed to fetch Stripe session:", err);
        
        // Fallback: Try localStorage as last resort (but log warning)
        const cartString = localStorage.getItem('qwytt_cart');
        const cart = cartString ? JSON.parse(cartString) : {};
        
        const fallbackTotal = Object.values(cart).reduce(
          (sum, item) => sum + (item.price * item.quantity),
          0
        ) / 100;

        // Fire with fallback data but flag it
        if (window.snaptr) {
          snaptr("track", "PURCHASE", {
            price: fallbackTotal || 149.99, // Use expected price if cart empty
            currency: "USD",
            transaction_id: sessionId,
            uuid_c1: getSnapClickId(),
          });
          console.warn("⚠️ Snap PURCHASE fired with fallback price:", fallbackTotal || 149.99);
        }

        logEvent(analytics, "purchase", {
          transaction_id: sessionId,
          currency: "USD",
          value: fallbackTotal || 149.99,
        });

        setStatus('success'); // Still show success to user
      } finally {
        // Clear cart regardless of outcome
        localStorage.removeItem('qwytt_cart');
        window.dispatchEvent(new Event('cartCleared'));
      }
    };

    fetchSessionAndTrack();
  }, [sessionId, analytics, functions]);

  const handleReturnHomeClick = () => {
    logEvent(analytics, "post_purchase_action", {
      action: "return_home",
      page: "purchase_success"
    });
  };

  // Loading state
  if (status === 'loading') {
    return (
      <div className={styles.page}>
        <section className={styles.hero}>
          <h1 className={styles.title}>Confirming your order...</h1>
          <p className={styles.subtitle}>Please wait a moment.</p>
        </section>
      </div>
    );
  }

  // Error state (no session ID)
  if (status === 'error' && !sessionId) {
    return (
      <div className={styles.page}>
        <section className={styles.hero}>
          <h1 className={styles.title}>Something went wrong</h1>
          <p className={styles.subtitle}>
            We couldn't find your order details. If you completed a purchase,
            please check your email for confirmation.
          </p>
        </section>
        <section className={styles.content}>
          <a className={styles.returnHome} href="/" onClick={handleReturnHomeClick}>
            Return Home
          </a>
        </section>
      </div>
    );
  }

  // Success state
  return (
    <div className={styles.page}>
      <section className={styles.hero}>
        <h1 className={styles.title}>Reservation Confirmed 🎉</h1>
        <p className={styles.subtitle}>
          Your early-access Qwytt device is officially reserved.
        </p>
        {orderDetails && (
          <p className={styles.orderSummary}>
            Order total: ${orderDetails.totalPrice.toFixed(2)} {orderDetails.currency}
          </p>
        )}
      </section>

      <section className={styles.content}>
        <div className={styles.card}>
          <h3 className={styles.cardTitle}>What's Next?</h3>
          <p className={styles.cardText}>
            We'll email you with updates on shipping, tracking, and early-access
            program details. Thank you for supporting Qwytt and being part of our 
            mission to help people quit nicotine.
          </p>
        </div>

        <a 
          className={styles.returnHome} 
          href="/"
          onClick={handleReturnHomeClick}
        >
          Return Home
        </a>
      </section>
    </div>
  );
};

export default PurchaseSuccess;